import {
    useCallback,
    useEffect,
    useState,
} from "react";

import {
    BadgeCheck,
    Check,
    FileText,
    Landmark,
    RefreshCw,
    ShieldCheck,
    UserRound,
    X,
} from "lucide-react";

import {
    toast,
} from "sonner";

import api, {
    formatApiError,
} from "@/lib/api";

import {
    useLang,
} from "@/contexts/LangContext";

import Layout from "@/components/Layout";

import {
    Button,
} from "@/components/ui/button";

import {
    Textarea,
} from "@/components/ui/textarea";


export default function AdminVerifications() {
    const {
        lang,
    } = useLang();

    const ar = lang === "ar";

    const [
        items,
        setItems,
    ] = useState([]);

    const [
        loading,
        setLoading,
    ] = useState(true);

    const [
        filter,
        setFilter,
    ] = useState("pending");

    const [
        actionId,
        setActionId,
    ] = useState(null);

    const [
        actionType,
        setActionType,
    ] = useState(null);

    const [
        reason,
        setReason,
    ] = useState("");

    const [
        processing,
        setProcessing,
    ] = useState(false);

    const [
        openingFile,
        setOpeningFile,
    ] = useState(null);


    const load = useCallback(
        async () => {
            setLoading(true);

            try {
                const endpoint =
                    filter === "pending"
                        ? "/admin/verifications"
                        : "/admin/verifications/all";

                const {
                    data,
                } = await api.get(
                    endpoint
                );

                setItems(
                    Array.isArray(data)
                        ? data
                        : []
                );

            } catch (err) {
                toast.error(
                    formatApiError(
                        err.response?.data?.detail
                    )
                    || (
                        ar
                            ? "\u062a\u0639\u0630\u0631 \u062a\u062d\u0645\u064a\u0644 \u0637\u0644\u0628\u0627\u062a \u0627\u0644\u0628\u0627\u0626\u0639\u064a\u0646"
                            : "Could not load seller applications"
                    )
                );

            } finally {
                setLoading(false);
            }
        },
        [
            ar,
            filter,
        ]
    );


    useEffect(() => {
        load();
    }, [load]);


    const sellerTypeLabel = (
        value
    ) => {
        if (
            value
            === "productive_family"
        ) {
            return ar
                ? "\u0623\u0633\u0631\u0629 \u0645\u0646\u062a\u062c\u0629"
                : "Productive Family";
        }

        if (
            value
            === "freelance"
        ) {
            return ar
                ? "\u0648\u062b\u064a\u0642\u0629 \u0639\u0645\u0644 \u062d\u0631"
                : "Freelance Document";
        }

        return ar
            ? "\u0633\u062c\u0644 \u062a\u062c\u0627\u0631\u064a"
            : "Commercial Registration";
    };


    const identityTypeLabel = (
        value
    ) => {
        return value === "iqama"
            ? (
                ar
                    ? "\u0625\u0642\u0627\u0645\u0629"
                    : "Iqama"
            )
            : (
                ar
                    ? "\u0647\u0648\u064a\u0629 \u0648\u0637\u0646\u064a\u0629"
                    : "National ID"
            );
    };


    const statusLabel = (
        value
    ) => {
        const labels = {
            pending: ar
                ? "\u0642\u064a\u062f \u0627\u0644\u0645\u0631\u0627\u062c\u0639\u0629"
                : "Pending",

            approved: ar
                ? "\u0645\u0648\u0627\u0641\u0642 \u0639\u0644\u064a\u0647"
                : "Approved",

            changes_required: ar
                ? "\u064a\u062d\u062a\u0627\u062c \u062a\u0639\u062f\u064a\u0644"
                : "Changes Required",

            rejected: ar
                ? "\u0645\u0631\u0641\u0648\u0636"
                : "Rejected",

            onboarding_required: ar
                ? "\u0644\u0645 \u064a\u0643\u0645\u0644 \u0627\u0644\u0637\u0644\u0628"
                : "Onboarding Required",
        };

        return labels[value]
            || value
            || "-";
    };


    const statusClass = (
        value
    ) => {
        if (
            value === "approved"
        ) {
            return (
                "bg-green-50 "
                + "text-green-700 "
                + "border-green-200"
            );
        }

        if (
            value === "rejected"
        ) {
            return (
                "bg-red-50 "
                + "text-red-700 "
                + "border-red-200"
            );
        }

        if (
            value
            === "changes_required"
        ) {
            return (
                "bg-orange-50 "
                + "text-orange-700 "
                + "border-orange-200"
            );
        }

        return (
            "bg-yellow-50 "
            + "text-yellow-700 "
            + "border-yellow-200"
        );
    };


    const openPrivateFile = async (
        fileId
    ) => {
        if (!fileId) {
            return;
        }

        setOpeningFile(
            fileId
        );

        try {
            const response =
                await api.get(
                    `/admin/verification-files/${fileId}`,
                    {
                        responseType:
                            "blob",
                    }
                );

            const blobUrl =
                URL.createObjectURL(
                    response.data
                );

            const anchor =
                document.createElement(
                    "a"
                );

            anchor.href =
                blobUrl;

            anchor.target =
                "_blank";

            anchor.rel =
                "noopener noreferrer";

            document.body.appendChild(
                anchor
            );

            anchor.click();

            anchor.remove();

            window.setTimeout(
                () => {
                    URL.revokeObjectURL(
                        blobUrl
                    );
                },
                60000
            );

        } catch (err) {
            toast.error(
                formatApiError(
                    err.response?.data?.detail
                )
                || (
                    ar
                        ? "\u062a\u0639\u0630\u0631 \u0641\u062a\u062d \u0627\u0644\u0645\u0633\u062a\u0646\u062f"
                        : "Could not open document"
                )
            );

        } finally {
            setOpeningFile(null);
        }
    };


    const approve = async (
        verificationId
    ) => {
        setProcessing(true);

        try {
            await api.post(
                `/admin/verifications/${verificationId}/approve`
            );

            toast.success(
                ar
                    ? "\u062a\u0645\u062a \u0627\u0644\u0645\u0648\u0627\u0641\u0642\u0629 \u0639\u0644\u0649 \u0627\u0644\u0628\u0627\u0626\u0639"
                    : "Seller approved"
            );

            setActionId(null);
            setActionType(null);
            setReason("");

            await load();

        } catch (err) {
            toast.error(
                formatApiError(
                    err.response?.data?.detail
                )
                || (
                    ar
                        ? "\u062a\u0639\u0630\u0631\u062a \u0627\u0644\u0645\u0648\u0627\u0641\u0642\u0629"
                        : "Could not approve seller"
                )
            );

        } finally {
            setProcessing(false);
        }
    };


    const submitReasonAction =
        async () => {
            if (
                !actionId
                || !actionType
            ) {
                return;
            }

            if (!reason.trim()) {
                toast.error(
                    ar
                        ? "\u0627\u0643\u062a\u0628 \u0627\u0644\u0633\u0628\u0628 \u0623\u0648\u0644\u0627"
                        : "Please enter a reason"
                );

                return;
            }

            setProcessing(true);

            try {
                const endpoint =
                    actionType
                    === "changes"
                        ? (
                            `/admin/verifications/${actionId}/request-changes`
                        )
                        : (
                            `/admin/verifications/${actionId}/reject`
                        );

                await api.post(
                    endpoint,
                    {
                        reason:
                            reason.trim(),
                    }
                );

                toast.success(
                    actionType
                    === "changes"
                        ? (
                            ar
                                ? "\u062a\u0645 \u0625\u0631\u0633\u0627\u0644 \u0637\u0644\u0628 \u0627\u0644\u062a\u0639\u062f\u064a\u0644"
                                : "Changes requested"
                        )
                        : (
                            ar
                                ? "\u062a\u0645 \u0631\u0641\u0636 \u0627\u0644\u0637\u0644\u0628"
                                : "Application rejected"
                        )
                );

                setActionId(null);
                setActionType(null);
                setReason("");

                await load();

            } catch (err) {
                toast.error(
                    formatApiError(
                        err.response?.data?.detail
                    )
                    || (
                        ar
                            ? "\u062a\u0639\u0630\u0631 \u062a\u0646\u0641\u064a\u0630 \u0627\u0644\u0625\u062c\u0631\u0627\u0621"
                            : "Action failed"
                    )
                );

            } finally {
                setProcessing(false);
            }
        };


    const startReasonAction = (
        verificationId,
        type
    ) => {
        setActionId(
            verificationId
        );

        setActionType(
            type
        );

        setReason("");
    };


    const cancelAction = () => {
        setActionId(null);
        setActionType(null);
        setReason("");
    };


    const DocumentButton = ({
        fileId,
        label,
    }) => {
        if (!fileId) {
            return null;
        }

        return (
            <Button
                type="button"
                variant="outline"
                disabled={
                    openingFile
                    === fileId
                }
                onClick={
                    () =>
                        openPrivateFile(
                            fileId
                        )
                }
                className="justify-start rounded-xl h-auto py-3"
            >
                <FileText className="w-4 h-4 me-2 text-terracotta" />

                <span>
                    {
                        openingFile
                        === fileId
                            ? "..."
                            : label
                    }
                </span>
            </Button>
        );
    };


    return (
        <Layout>
            <div className="max-w-6xl mx-auto px-4 md:px-8 py-10">

                <div className="flex flex-wrap items-center justify-between gap-4">
                    <div>
                        <h1 className="text-3xl md:text-4xl font-black text-espresso">
                            {
                                ar
                                    ? "\u0637\u0644\u0628\u0627\u062a \u062a\u0648\u062b\u064a\u0642 \u0627\u0644\u0628\u0627\u0626\u0639\u064a\u0646"
                                    : "Seller Verification Applications"
                            }
                        </h1>

                        <p className="text-sm text-muted-foreground mt-2">
                            {
                                ar
                                    ? "\u0631\u0627\u062c\u0639 \u0628\u064a\u0627\u0646\u0627\u062a \u0627\u0644\u0628\u0627\u0626\u0639 \u0648\u0645\u0633\u062a\u0646\u062f\u0627\u062a\u0647 \u0642\u0628\u0644 \u0627\u0644\u0633\u0645\u0627\u062d \u0644\u0647 \u0628\u0641\u062a\u062d \u0627\u0644\u0645\u062a\u062c\u0631."
                                    : "Review seller details and documents before allowing store access."
                            }
                        </p>
                    </div>

                    <Button
                        type="button"
                        variant="outline"
                        onClick={load}
                        className="rounded-full"
                    >
                        <RefreshCw className="w-4 h-4 me-2" />

                        {
                            ar
                                ? "\u062a\u062d\u062f\u064a\u062b"
                                : "Refresh"
                        }
                    </Button>
                </div>


                <div className="mt-6 flex gap-2">
                    <Button
                        type="button"
                        variant={
                            filter
                            === "pending"
                                ? "default"
                                : "outline"
                        }
                        onClick={
                            () =>
                                setFilter(
                                    "pending"
                                )
                        }
                        className="rounded-full"
                    >
                        {
                            ar
                                ? "\u0642\u064a\u062f \u0627\u0644\u0645\u0631\u0627\u062c\u0639\u0629"
                                : "Pending"
                        }
                    </Button>

                    <Button
                        type="button"
                        variant={
                            filter
                            === "all"
                                ? "default"
                                : "outline"
                        }
                        onClick={
                            () =>
                                setFilter(
                                    "all"
                                )
                        }
                        className="rounded-full"
                    >
                        {
                            ar
                                ? "\u062c\u0645\u064a\u0639 \u0627\u0644\u0637\u0644\u0628\u0627\u062a"
                                : "All Applications"
                        }
                    </Button>
                </div>


                {
                    loading
                        ? (
                            <div className="py-20 text-center text-muted-foreground">
                                ...
                            </div>
                        )
                        : (
                            items.length
                            === 0
                                ? (
                                    <div className="mt-8 py-16 text-center text-muted-foreground bg-surface rounded-2xl">
                                        {
                                            ar
                                                ? "\u0644\u0627 \u062a\u0648\u062c\u062f \u0637\u0644\u0628\u0627\u062a"
                                                : "No applications found"
                                        }
                                    </div>
                                )
                                : (
                                    <div
                                        className="mt-8 space-y-6"
                                        data-testid="seller-verifications-list"
                                    >
                                        {
                                            items.map(
                                                (item) => (
                                                    <div
                                                        key={
                                                            item.verification_id
                                                        }
                                                        className="bg-white border border-border rounded-3xl p-6 md:p-8"
                                                    >

                                                        <div className="flex flex-wrap items-start justify-between gap-4">
                                                            <div>
                                                                <div className="flex items-center gap-2">
                                                                    <UserRound className="w-5 h-5 text-terracotta" />

                                                                    <h2 className="text-xl font-black text-espresso">
                                                                        {
                                                                            item.legal_name
                                                                            || item.account_name
                                                                            || "-"
                                                                        }
                                                                    </h2>
                                                                </div>

                                                                <div className="text-sm text-muted-foreground mt-2">
                                                                    {
                                                                        item.email
                                                                        || "-"
                                                                    }
                                                                </div>

                                                                <div className="text-sm text-muted-foreground">
                                                                    {
                                                                        item.phone
                                                                        || "-"
                                                                    }
                                                                </div>
                                                            </div>

                                                            <span
                                                                className={`border rounded-full px-3 py-1 text-xs font-bold ${statusClass(item.status)}`}
                                                            >
                                                                {
                                                                    statusLabel(
                                                                        item.status
                                                                    )
                                                                }
                                                            </span>
                                                        </div>


                                                        <div className="mt-6 grid lg:grid-cols-2 gap-6">

                                                            <div className="bg-surface rounded-2xl p-5">
                                                                <div className="flex items-center gap-2 font-black text-espresso mb-4">
                                                                    <ShieldCheck className="w-5 h-5 text-terracotta" />

                                                                    {
                                                                        ar
                                                                            ? "\u0628\u064a\u0627\u0646\u0627\u062a \u0627\u0644\u0647\u0648\u064a\u0629 \u0648\u0627\u0644\u0646\u0634\u0627\u0637"
                                                                            : "Identity & Business"
                                                                    }
                                                                </div>

                                                                <div className="space-y-2 text-sm">
                                                                    <div>
                                                                        <strong>
                                                                            {
                                                                                ar
                                                                                    ? "\u0646\u0648\u0639 \u0627\u0644\u0628\u0627\u0626\u0639: "
                                                                                    : "Seller type: "
                                                                            }
                                                                        </strong>

                                                                        {
                                                                            sellerTypeLabel(
                                                                                item.seller_type
                                                                            )
                                                                        }
                                                                    </div>

                                                                    <div>
                                                                        <strong>
                                                                            {
                                                                                ar
                                                                                    ? "\u0646\u0648\u0639 \u0627\u0644\u0647\u0648\u064a\u0629: "
                                                                                    : "Identity type: "
                                                                            }
                                                                        </strong>

                                                                        {
                                                                            identityTypeLabel(
                                                                                item.identity_type
                                                                            )
                                                                        }
                                                                    </div>

                                                                    <div>
                                                                        <strong>
                                                                            {
                                                                                ar
                                                                                    ? "\u0631\u0642\u0645 \u0627\u0644\u0647\u0648\u064a\u0629: "
                                                                                    : "ID number: "
                                                                            }
                                                                        </strong>

                                                                        <span dir="ltr">
                                                                            {
                                                                                item.identity_number
                                                                                || "-"
                                                                            }
                                                                        </span>
                                                                    </div>

                                                                    <div>
                                                                        <strong>
                                                                            {
                                                                                ar
                                                                                    ? "\u0627\u0633\u0645 \u0627\u0644\u0645\u0634\u0631\u0648\u0639: "
                                                                                    : "Business name: "
                                                                            }
                                                                        </strong>

                                                                        {
                                                                            item.business_name
                                                                            || "-"
                                                                        }
                                                                    </div>

                                                                    <div>
                                                                        <strong>
                                                                            {
                                                                                ar
                                                                                    ? "\u0627\u0644\u0646\u0634\u0627\u0637: "
                                                                                    : "Activity: "
                                                                            }
                                                                        </strong>

                                                                        {
                                                                            item.activity_name
                                                                            || "-"
                                                                        }
                                                                    </div>

                                                                    <div>
                                                                        <strong>
                                                                            {
                                                                                ar
                                                                                    ? "\u0627\u0644\u0645\u062f\u064a\u0646\u0629: "
                                                                                    : "City: "
                                                                            }
                                                                        </strong>

                                                                        {
                                                                            item.city
                                                                            || "-"
                                                                        }
                                                                    </div>

                                                                    <div>
                                                                        <strong>
                                                                            {
                                                                                ar
                                                                                    ? "\u0631\u0642\u0645 \u0648\u062b\u064a\u0642\u0629 \u0627\u0644\u0646\u0634\u0627\u0637: "
                                                                                    : "Business document number: "
                                                                            }
                                                                        </strong>

                                                                        <span dir="ltr">
                                                                            {
                                                                                item.business_document_number
                                                                                || "-"
                                                                            }
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                            </div>


                                                            <div className="bg-surface rounded-2xl p-5">
                                                                <div className="flex items-center gap-2 font-black text-espresso mb-4">
                                                                    <Landmark className="w-5 h-5 text-terracotta" />

                                                                    {
                                                                        ar
                                                                            ? "\u0627\u0644\u062d\u0633\u0627\u0628 \u0627\u0644\u0628\u0646\u0643\u064a \u0648\u0627\u0644\u0636\u0631\u064a\u0628\u0629"
                                                                            : "Bank & VAT"
                                                                    }
                                                                </div>

                                                                <div className="space-y-2 text-sm">
                                                                    <div>
                                                                        <strong>
                                                                            {
                                                                                ar
                                                                                    ? "\u0627\u0633\u0645 \u0627\u0644\u0628\u0646\u0643: "
                                                                                    : "Bank: "
                                                                            }
                                                                        </strong>

                                                                        {
                                                                            item.bank_name
                                                                            || "-"
                                                                        }
                                                                    </div>

                                                                    <div>
                                                                        <strong>
                                                                            {
                                                                                ar
                                                                                    ? "\u0635\u0627\u062d\u0628 \u0627\u0644\u062d\u0633\u0627\u0628: "
                                                                                    : "Account holder: "
                                                                            }
                                                                        </strong>

                                                                        {
                                                                            item.bank_account_name
                                                                            || "-"
                                                                        }
                                                                    </div>

                                                                    <div>
                                                                        <strong>
                                                                            IBAN:
                                                                            {" "}
                                                                        </strong>

                                                                        <span dir="ltr">
                                                                            {
                                                                                item.iban
                                                                                || "-"
                                                                            }
                                                                        </span>
                                                                    </div>

                                                                    <div>
                                                                        <strong>
                                                                            {
                                                                                ar
                                                                                    ? "\u0645\u0633\u062c\u0644 \u0641\u064a VAT: "
                                                                                    : "VAT registered: "
                                                                            }
                                                                        </strong>

                                                                        {
                                                                            item.vat_registered
                                                                                ? (
                                                                                    ar
                                                                                        ? "\u0646\u0639\u0645"
                                                                                        : "Yes"
                                                                                )
                                                                                : (
                                                                                    ar
                                                                                        ? "\u0644\u0627"
                                                                                        : "No"
                                                                                )
                                                                        }
                                                                    </div>

                                                                    {
                                                                        item.vat_registered
                                                                        && (
                                                                            <div>
                                                                                <strong>
                                                                                    {
                                                                                        ar
                                                                                            ? "\u0627\u0644\u0631\u0642\u0645 \u0627\u0644\u0636\u0631\u064a\u0628\u064a: "
                                                                                            : "VAT number: "
                                                                                    }
                                                                                </strong>

                                                                                <span dir="ltr">
                                                                                    {
                                                                                        item.vat_number
                                                                                        || "-"
                                                                                    }
                                                                                </span>
                                                                            </div>
                                                                        )
                                                                    }
                                                                </div>
                                                            </div>

                                                        </div>


                                                        <div className="mt-6">
                                                            <div className="font-black text-espresso mb-3">
                                                                {
                                                                    ar
                                                                        ? "\u0627\u0644\u0645\u0633\u062a\u0646\u062f\u0627\u062a"
                                                                        : "Documents"
                                                                }
                                                            </div>

                                                            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                                                                <DocumentButton
                                                                    fileId={
                                                                        item.identity_file_id
                                                                    }
                                                                    label={
                                                                        ar
                                                                            ? "\u0627\u0644\u0647\u0648\u064a\u0629 / \u0627\u0644\u0625\u0642\u0627\u0645\u0629"
                                                                            : "Identity"
                                                                    }
                                                                />

                                                                <DocumentButton
                                                                    fileId={
                                                                        item.business_document_file_id
                                                                    }
                                                                    label={
                                                                        ar
                                                                            ? "\u0648\u062b\u064a\u0642\u0629 \u0627\u0644\u0646\u0634\u0627\u0637"
                                                                            : "Business Document"
                                                                    }
                                                                />

                                                                <DocumentButton
                                                                    fileId={
                                                                        item.iban_certificate_file_id
                                                                    }
                                                                    label={
                                                                        ar
                                                                            ? "\u0634\u0647\u0627\u062f\u0629 IBAN"
                                                                            : "IBAN Certificate"
                                                                    }
                                                                />

                                                                {
                                                                    item.vat_registered
                                                                    && (
                                                                        <DocumentButton
                                                                            fileId={
                                                                                item.vat_certificate_file_id
                                                                            }
                                                                            label={
                                                                                ar
                                                                                    ? "\u0634\u0647\u0627\u062f\u0629 VAT"
                                                                                    : "VAT Certificate"
                                                                            }
                                                                        />
                                                                    )
                                                                }
                                                            </div>
                                                        </div>


                                                        {
                                                            item.admin_note
                                                            && (
                                                                <div className="mt-6 bg-orange-50 border border-orange-200 rounded-2xl p-4">
                                                                    <strong>
                                                                        {
                                                                            ar
                                                                                ? "\u0645\u0644\u0627\u062d\u0638\u0629 \u0627\u0644\u0625\u062f\u0627\u0631\u0629:"
                                                                                : "Admin note:"
                                                                        }
                                                                    </strong>

                                                                    <div className="mt-1 text-sm">
                                                                        {
                                                                            item.admin_note
                                                                        }
                                                                    </div>
                                                                </div>
                                                            )
                                                        }


                                                        {
                                                            item.status
                                                            === "pending"
                                                            && (
                                                                <div className="mt-6 border-t border-border pt-5">

                                                                    <div className="flex flex-wrap gap-3">
                                                                        <Button
                                                                            type="button"
                                                                            disabled={processing}
                                                                            onClick={
                                                                                () =>
                                                                                    approve(
                                                                                        item.verification_id
                                                                                    )
                                                                            }
                                                                            className="rounded-full bg-green-600 hover:bg-green-700 text-white"
                                                                        >
                                                                            <Check className="w-4 h-4 me-2" />

                                                                            {
                                                                                ar
                                                                                    ? "\u0645\u0648\u0627\u0641\u0642\u0629"
                                                                                    : "Approve"
                                                                            }
                                                                        </Button>

                                                                        <Button
                                                                            type="button"
                                                                            variant="outline"
                                                                            disabled={processing}
                                                                            onClick={
                                                                                () =>
                                                                                    startReasonAction(
                                                                                        item.verification_id,
                                                                                        "changes"
                                                                                    )
                                                                            }
                                                                            className="rounded-full"
                                                                        >
                                                                            <RefreshCw className="w-4 h-4 me-2" />

                                                                            {
                                                                                ar
                                                                                    ? "\u0637\u0644\u0628 \u062a\u0639\u062f\u064a\u0644"
                                                                                    : "Request Changes"
                                                                            }
                                                                        </Button>

                                                                        <Button
                                                                            type="button"
                                                                            variant="outline"
                                                                            disabled={processing}
                                                                            onClick={
                                                                                () =>
                                                                                    startReasonAction(
                                                                                        item.verification_id,
                                                                                        "reject"
                                                                                    )
                                                                            }
                                                                            className="rounded-full border-red-300 text-red-600 hover:bg-red-50"
                                                                        >
                                                                            <X className="w-4 h-4 me-2" />

                                                                            {
                                                                                ar
                                                                                    ? "\u0631\u0641\u0636"
                                                                                    : "Reject"
                                                                            }
                                                                        </Button>
                                                                    </div>


                                                                    {
                                                                        actionId
                                                                        === item.verification_id
                                                                        && actionType
                                                                        && (
                                                                            <div className="mt-4 bg-surface rounded-2xl p-4">
                                                                                <div className="font-bold text-sm text-espresso">
                                                                                    {
                                                                                        actionType
                                                                                        === "changes"
                                                                                            ? (
                                                                                                ar
                                                                                                    ? "\u0645\u0627 \u0627\u0644\u0630\u064a \u064a\u062c\u0628 \u0639\u0644\u0649 \u0627\u0644\u0628\u0627\u0626\u0639 \u062a\u0639\u062f\u064a\u0644\u0647\u061f"
                                                                                                    : "What should the seller change?"
                                                                                            )
                                                                                            : (
                                                                                                ar
                                                                                                    ? "\u0645\u0627 \u0633\u0628\u0628 \u0631\u0641\u0636 \u0627\u0644\u0637\u0644\u0628\u061f"
                                                                                                    : "Why is the application being rejected?"
                                                                                            )
                                                                                    }
                                                                                </div>

                                                                                <Textarea
                                                                                    value={reason}
                                                                                    onChange={
                                                                                        (event) =>
                                                                                            setReason(
                                                                                                event.target.value
                                                                                            )
                                                                                    }
                                                                                    rows={3}
                                                                                    className="mt-2"
                                                                                />

                                                                                <div className="mt-3 flex gap-2">
                                                                                    <Button
                                                                                        type="button"
                                                                                        disabled={processing}
                                                                                        onClick={
                                                                                            submitReasonAction
                                                                                        }
                                                                                        className={
                                                                                            actionType
                                                                                            === "reject"
                                                                                                ? "rounded-full bg-red-600 hover:bg-red-700 text-white"
                                                                                                : "rounded-full"
                                                                                        }
                                                                                    >
                                                                                        {
                                                                                            processing
                                                                                                ? "..."
                                                                                                : (
                                                                                                    ar
                                                                                                        ? "\u0625\u0631\u0633\u0627\u0644"
                                                                                                        : "Submit"
                                                                                                )
                                                                                        }
                                                                                    </Button>

                                                                                    <Button
                                                                                        type="button"
                                                                                        variant="outline"
                                                                                        disabled={processing}
                                                                                        onClick={cancelAction}
                                                                                        className="rounded-full"
                                                                                    >
                                                                                        {
                                                                                            ar
                                                                                                ? "\u0625\u0644\u063a\u0627\u0621"
                                                                                                : "Cancel"
                                                                                        }
                                                                                    </Button>
                                                                                </div>
                                                                            </div>
                                                                        )
                                                                    }

                                                                </div>
                                                            )
                                                        }


                                                        {
                                                            item.status
                                                            === "approved"
                                                            && (
                                                                <div className="mt-6 flex items-center gap-2 text-green-700 font-bold">
                                                                    <BadgeCheck className="w-5 h-5" />

                                                                    {
                                                                        ar
                                                                            ? "\u062a\u0645 \u0627\u0639\u062a\u0645\u0627\u062f \u0647\u0630\u0627 \u0627\u0644\u0628\u0627\u0626\u0639"
                                                                            : "This seller is approved"
                                                                    }
                                                                </div>
                                                            )
                                                        }

                                                    </div>
                                                )
                                            )
                                        }
                                    </div>
                                )
                        )
                }

            </div>
        </Layout>
    );
}
